import { AssetLoader } from '../asset/AssetLoader';
import type { Disposable } from '../core/Event';
import { PlayerProfile } from '../model/profile/PlayerProfile';
import { WheelGameModel } from '../model/wheel/WheelGameModel';
import { DEFAULT_THEME, type Theme } from '../theme';
import { WheelOfFortune } from '../widgets/wheel/WheelOfFortune';
import { settleWheel, type WheelSettlement } from '../widgets/wheel/settleWheel';
import { WheelHudComponent } from './components/WheelHudComponent';
import { WheelWinSequenceComponent } from './components/WheelWinSequenceComponent';
import { DEFAULT_WHEEL_CONFIG } from './fixtures/wheelDemoFixtures';
import { SceneBase } from './SceneBase';
import type { SceneLayoutContext } from './SceneComponent';

const STARTING_BALANCE = 1000;
const BET_PER_SPIN = 50;

/**
 * Wheel-of-fortune demo scene. Bet 50 coins; spin the wheel; segment
 * multiplier × bet = payout. ✕ wedges return 0; landing on a bankrupt
 * segment wipes the balance.
 *
 * <p>Pixi-app lifecycle + strict-mode-unmount handling come from
 * {@link SceneBase}. Game state is on {@link WheelGameModel}; the
 * {@link WheelHudComponent} subscribes directly so balance updates are
 * automatic. Win celebration is owned by {@link WheelWinSequenceComponent}.
 */
export class WheelDemoScene extends SceneBase {
  private wheel: WheelOfFortune | null = null;
  private hud: WheelHudComponent | null = null;
  private winSequence: WheelWinSequenceComponent | null = null;
  private spinning = false;
  private readonly model: WheelGameModel;
  private profileSubscription: Disposable | null = null;

  constructor(
    container: HTMLElement,
    theme: Theme = DEFAULT_THEME,
    profile: PlayerProfile | null = null,
  ) {
    super(container, { theme });
    this.model = new WheelGameModel({
      startingBalance: profile?.balance ?? STARTING_BALANCE,
      betPerSpin: BET_PER_SPIN,
    });
    if (profile) {
      this.profileSubscription = this.model.events.on('balance-changed', () => {
        profile.setBalance(this.model.balance);
      });
    }
  }

  protected async preload(): Promise<void> {
    await AssetLoader.shared.loadTheme(this.theme);
  }

  protected buildContent(): void {
    this.wheel = new WheelOfFortune({
      ...DEFAULT_WHEEL_CONFIG,
      segments: this.theme.wheel.segments,
      chrome: {
        accentColor: this.theme.palette.accent,
        frameFillColor: this.theme.palette.frameFill,
        pointerFillColor: this.theme.palette.warm,
      },
    });
    this.app.stage.addChild(this.wheel);

    this.winSequence = new WheelWinSequenceComponent(this.app.ticker, {
      fillColor: this.theme.palette.warm,
      strokeColor: this.theme.palette.accent,
    });
    this.app.stage.addChild(this.winSequence);

    this.hud = new WheelHudComponent(this.model, {
      onSpin: () => void this.handleSpin(),
    });
    this.app.stage.addChild(this.hud);
  }

  protected onResize(): void {
    this.layout();
  }

  protected onKeyDown(event: KeyboardEvent): void {
    if (event.code === 'Space') {
      event.preventDefault();
      void this.handleSpin();
    }
  }

  protected disposeResources(wasInitialized: boolean): void {
    this.profileSubscription?.dispose();
    this.profileSubscription = null;
    this.model.dispose();
    if (!wasInitialized) return;
    this.hud?.destroy();
    this.hud = null;
    this.winSequence?.destroy();
    this.winSequence = null;
    if (this.wheel) {
      this.wheel.destroyWheel();
      this.wheel = null;
    }
  }

  private layout(): void {
    if (!this.wheel || !this.hud || !this.winSequence) return;

    const { width, height } = this.app.renderer;
    this.wheel.x = Math.round(width / 2);
    this.wheel.y = Math.round(height / 2 - 32);

    const wheelTop = this.wheel.y - DEFAULT_WHEEL_CONFIG.radius;
    const wheelBottom = this.wheel.y + DEFAULT_WHEEL_CONFIG.radius;
    const ctx: SceneLayoutContext = {
      width,
      height,
      centerX: Math.round(width / 2),
      machineTop: wheelTop,
      machineBottom: wheelBottom,
      spinButtonY: Math.round(wheelBottom + 56),
    };
    this.hud.layout(ctx);
    this.winSequence.layout(ctx);
  }

  private async handleSpin(): Promise<void> {
    if (!this.wheel || !this.hud || !this.winSequence || this.spinning) return;
    if (!this.model.tryDebitBet()) return;

    this.spinning = true;
    this.hud.setSpinning(true);
    this.hud.setStatusText(`bet ${this.model.betPerSpin}…`);

    const outcome = await this.wheel.spin();
    const settlement = settleWheel(outcome, this.model.betPerSpin);

    if (settlement.winnings > 0) {
      this.model.creditWinnings(settlement.winnings);
      this.winSequence.playWin(settlement);
      this.hud.setStatusText(describeOutcome(settlement));
    } else if (settlement.isBankrupt) {
      this.model.bankrupt();
      this.hud.setStatusText(`💀 BANKRUPT — balance wiped (refresh to reset)`);
    } else {
      this.hud.setStatusText(`landed on ${settlement.segment.label} → no win`);
    }

    this.model.emitLanded(settlement.winnings, settlement.isBankrupt);
    this.hud.setSpinning(false);
    this.spinning = false;
  }
}

function describeOutcome(settlement: WheelSettlement): string {
  return `landed on ${settlement.segment.label} → +${settlement.winnings.toLocaleString()}`;
}
